<template>
  <div class="mention-textarea-wrapper">
    <div
      ref="editorRef"
      class="mention-editor"
      contenteditable="true"
      @input="handleInput"
      @keydown="handleKeydown"
      @click="handleClick"
      @focus="hasFocus = true"
      @blur="handleBlur"
      @paste.prevent="handlePaste"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
      :data-placeholder="!editorContent ? placeholder : ''"
    ></div>

    <!-- Mention Dropdown - Teleported to body to escape stacking contexts -->
    <Teleport to="body">
      <div
        v-if="showDropdown"
        class="mention-dropdown"
        :style="dropdownStyle"
      >
        <div
          v-if="filteredCustomers.length === 0"
          class="mention-no-results"
        >
          No matching providers
        </div>
        <div
          v-for="(customer, index) in filteredCustomers"
          :key="customer.customer_id"
          class="mention-item"
          :class="{ 'mention-item-selected': selectedIndex === index }"
          @click="selectMention(customer)"
          @mouseenter="selectedIndex = index"
        >
          {{ customer.name }}
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { notify } from '@/utils/notify';
import { getJwtTokenCustomerList } from '@/api/internalNotes';

export default {
  name: 'MentionTextarea',
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Type your notes here ...',
    },
    customerNameMap: {
      type: Object,
      default: () => ({}),
    },
    customerList: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      customerListData: [], // Array of { customer_id, name }
      showDropdown: false,
      dropdownPosition: { top: 0, left: 0 },
      searchTerm: '',
      mentionStartNode: null, // Where the @ symbol is (DOM node)
      mentionStartOffset: -1, // Offset within the node
      selectedIndex: 0, // For keyboard navigation
      mentions: [], // Track inserted mentions: { customer_id, name, clinic_id }
      hasFocus: false,
      editorContent: '', // Plain text content
    };
  },
  computed: {
    ...mapState('login', ['userInfo']),

    filteredCustomers() {
      if (!this.searchTerm) return this.customerListData;
      const search = this.searchTerm.toLowerCase();
      return this.customerListData.filter(c =>
        c.name.toLowerCase().includes(search) ||
        c.customer_id.includes(search)
      );
    },

    dropdownStyle() {
      return {
        top: `${this.dropdownPosition.top}px`,
        left: `${this.dropdownPosition.left}px`,
      };
    },
  },
  watch: {
    modelValue: {
      handler(newVal) {
        // Always update if value is empty (clearing the input)
        if (!newVal) {
          this.setContent('');
          return;
        }

        // Only update from external changes if we don't have focus
        // This prevents resetting innerHTML while user is typing
        if (!this.hasFocus && newVal !== this.getPlainText()) {
          this.setContent(newVal);
        }
      },
      immediate: true
    }
  },
  async mounted() {
    // Use customerList from prop if provided, otherwise load from API (backward compatibility)
    if (this.customerList && this.customerList.length > 0) {
      this.customerListData = this.customerList;
    } else {
      await this.loadCustomerList();
    }
    this.setContent(this.modelValue);
  },
  methods: {
    async loadCustomerList() {
      try {
        const response = await getJwtTokenCustomerList();
        // Convert [{ "999997": "Vibrant IT3333" }] to [{ customer_id: "999997", name: "Vibrant IT3333" }]
        this.customerListData = response.flatMap(item => {
          return Object.entries(item).map(([customer_id, name]) => ({
            customer_id,
            name
          }));
        });
      } catch (error) {
        console.error('Failed to load customer list for mentions:', error);
      }
    },

    setContent(text) {
      if (!this.$refs.editorRef) return;

      if (!text) {
        // Clearing the content
        this.$refs.editorRef.innerHTML = '';
        this.editorContent = '';
        return;
      }

      // Check if editor already has mention spans
      const hasMentionSpans = this.$refs.editorRef.querySelectorAll('.mention-highlight').length > 0;

      if (hasMentionSpans) {
        // Don't rebuild innerHTML if we have mentions - preserve existing HTML
        // Just update the plain text content for character counter
        this.editorContent = text;
      } else {
        // For new content, check if it has backend format mentions
        const hasBackendMentions = /@doctor_\w+_\w+/.test(text);

        if (hasBackendMentions && Object.keys(this.customerNameMap).length > 0) {
          // Transform backend format to display format with highlight spans
          const displayHtml = this.transformBackendToDisplay(text);
          this.$refs.editorRef.innerHTML = displayHtml;

          // For character counter, use plain text (without HTML)
          this.editorContent = this.getDisplayTextFromBackend(text);
          this.placeCaretAtEnd();
        } else {
          // No mentions or no name map, just set text directly
          this.$refs.editorRef.textContent = text;
          this.editorContent = text;
          this.placeCaretAtEnd();
        }
      }
    },

    // Transform backend format (@doctor_customerId_clinicId) to display HTML with highlight spans
    transformBackendToDisplay(backendText) {
      if (!backendText) return '';

      // Escape HTML first
      let escaped = backendText
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      // Replace @doctor_CUSTOMER_ID_CLINIC_ID with highlight spans
      const mentionRegex = /@doctor_(\w+)_(\w+)/g;
      const transformed = escaped.replace(mentionRegex, (match, customerId, clinicId) => {
        // Get the provider name from customerNameMap prop
        const providerName = this.customerNameMap[customerId];

        if (providerName) {
          return `<span class="mention-highlight" contenteditable="false" data-customer-id="${customerId}" data-clinic-id="${clinicId}">@${this.escapeHtml(providerName)}</span>`;
        } else {
          // Fallback: keep backend format if name not found
          return match;
        }
      });

      // Add space after line breaks for better formatting
      return transformed.replace(/\n/g, '<br>');
    },

    // Get display text (replace mentions with provider names)
    getDisplayTextFromBackend(backendText) {
      if (!backendText) return '';

      // Replace @doctor_CUSTOMER_ID_CLINIC_ID with @ProviderName
      const mentionRegex = /@doctor_(\w+)_\w+/g;
      return backendText.replace(mentionRegex, (match, customerId) => {
        const providerName = this.customerNameMap[customerId];
        return providerName ? `@${providerName}` : match;
      });
    },

    getPlainText() {
      if (!this.$refs.editorRef) return '';
      return this.$refs.editorRef.innerText || '';
    },

    highlightMentions(text) {
      // Find all @mention patterns and wrap them in spans
      // For now, just return the text - mentions will be highlighted as they're typed
      return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    },

    placeCaretAtEnd() {
      const editor = this.$refs.editorRef;
      if (!editor) return;

      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(editor);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
      editor.focus();
    },

    handleBlur() {
      // Delay to allow click events on dropdown to process
      setTimeout(() => {
        this.hasFocus = false;
        this.showDropdown = false;
      }, 200);
    },

    handleInput() {
      const editor = this.$refs.editorRef;
      const text = editor.innerText || '';

      // Check if we have mention spans (special content that shouldn't be overwritten)
      const hasMentionSpans = editor.querySelectorAll('.mention-highlight').length > 0;

      // Always update editorContent for character counter
      this.editorContent = text;

      if (!hasMentionSpans) {
        // No mentions, just emit the plain text
        this.$emit('update:modelValue', text);
      } else {
        // We have mentions, need to construct backend format from DOM
        const backendContent = this.buildBackendFormat();
        this.$emit('update:modelValue', backendContent);
      }

      // Check if we should show mention dropdown
      this.checkForMentionTrigger();
    },

    // Build backend format by traversing DOM and converting mentions to @doctor_id_clinicId format
    buildBackendFormat() {
      const editor = this.$refs.editorRef;
      if (!editor) return '';

      let backendContent = '';

      function traverseAndBuild(node) {
        if (node.nodeType === Node.TEXT_NODE) {
          backendContent += node.textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.classList && node.classList.contains('mention-highlight')) {
            // This is a mention span, get its backend format
            const customerId = node.dataset.customerId;
            const clinicId = node.dataset.clinicId;
            const mentionFormat = `@doctor_${customerId}_${clinicId}`;
            backendContent += mentionFormat;
          } else {
            // Regular element, traverse children
            for (let child of node.childNodes) {
              traverseAndBuild(child);
            }
          }
        }
      }

      traverseAndBuild(editor);

      return backendContent;
    },

    handleKeydown(event) {
      // Handle dropdown navigation
      if (this.showDropdown) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredCustomers.length - 1);
            return;
          case 'ArrowUp':
            event.preventDefault();
            this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
            return;
          case 'Enter':
          case 'Tab':
            event.preventDefault();
            if (this.filteredCustomers[this.selectedIndex]) {
              this.selectMention(this.filteredCustomers[this.selectedIndex]);
            }
            return;
          case 'Escape':
            event.preventDefault();
            this.showDropdown = false;
            return;
        }
      }

      // Handle mention deletion with backspace
      if (event.key === 'Backspace' || event.key === 'Delete') {
        this.handleDeleteKey(event);
        return;
      }
    },

    handleClick() {
      // Close dropdown when clicking
      this.showDropdown = false;
    },

    handleDeleteKey(event) {
      const editor = this.$refs.editorRef;
      if (!editor) return;

      const sel = window.getSelection();
      if (!sel.rangeCount) return;

      const range = sel.getRangeAt(0);

      // Check if cursor is inside or adjacent to a mention span
      let mentionToDelete = null;

      // First, check if the cursor is currently inside a mention span
      let currentNode = range.startContainer;
      if (currentNode.nodeType === Node.TEXT_NODE) {
        currentNode = currentNode.parentElement;
      }

      if (currentNode && currentNode.classList && currentNode.classList.contains('mention-highlight')) {
        // Cursor is inside a mention span, delete the entire mention
        mentionToDelete = currentNode;
      } else if (event.key === 'Backspace') {
        // Check if there's a mention span right before cursor
        let node = range.startContainer;
        if (node.nodeType === Node.TEXT_NODE) {
          // Check if we're at the start of a text node
          if (range.startOffset === 0) {
            // Check previous sibling
            let prevSibling = node.previousSibling;
            while (prevSibling) {
              if (prevSibling.nodeType === Node.ELEMENT_NODE &&
                  prevSibling.classList &&
                  prevSibling.classList.contains('mention-highlight')) {
                mentionToDelete = prevSibling;
                break;
              }
              if (prevSibling.nodeType === Node.TEXT_NODE && prevSibling.textContent.trim()) {
                // There's text before us, don't delete mention
                break;
              }
              prevSibling = prevSibling.previousSibling;
            }
          }
        }
      } else if (event.key === 'Delete') {
        // Check if there's a mention span right after cursor
        let node = range.startContainer;
        if (node.nodeType === Node.TEXT_NODE) {
          // Check if we're at the end of a text node
          if (range.startOffset === node.length) {
            // Check next sibling
            let nextSibling = node.nextSibling;
            while (nextSibling) {
              if (nextSibling.nodeType === Node.ELEMENT_NODE &&
                  nextSibling.classList &&
                  nextSibling.classList.contains('mention-highlight')) {
                mentionToDelete = nextSibling;
                break;
              }
              if (nextSibling.nodeType === Node.TEXT_NODE && nextSibling.textContent.trim()) {
                // There's text after us, don't delete mention
                break;
              }
              nextSibling = nextSibling.nextSibling;
            }
          }
        }
      }

      if (mentionToDelete) {
        event.preventDefault();
        // Remove the mention span
        mentionToDelete.remove();
        // Update content with backend format
        const backendContent = this.buildBackendFormat();
        this.editorContent = backendContent;
        this.$emit('update:modelValue', backendContent);
        // Keep focus
        editor.focus();
      }
    },

    checkForMentionTrigger() {
      const editor = this.$refs.editorRef;
      const sel = window.getSelection();
      if (!sel.rangeCount) return;

      const text = editor.innerText || '';
      const cursorPosition = this.getCaretPosition(editor);

      // Find the last @ symbol before cursor
      let atPosition = -1;
      for (let i = cursorPosition - 1; i >= 0; i--) {
        if (text[i] === '@') {
          atPosition = i;
          break;
        }
        // Stop if we hit a space or newline
        if (text[i] === ' ' || text[i] === '\n') {
          break;
        }
      }

      if (atPosition !== -1) {
        // We found an @ symbol
        const textAfterAt = text.substring(atPosition + 1, cursorPosition);

        // Check if there's a space or newline between @ and cursor
        const hasSpaceBetween = textAfterAt.includes(' ') || textAfterAt.includes('\n');

        if (!hasSpaceBetween) {
          // Show dropdown and highlight @ symbol
          this.mentionStartOffset = atPosition;
          this.searchTerm = textAfterAt;
          this.selectedIndex = 0;
          this.highlightAtSymbol();
          this.calculateDropdownPosition();
          this.showDropdown = true;
          return;
        }
      }

      // No valid @ mention found, hide dropdown
      this.showDropdown = false;
    },

    highlightAtSymbol() {
      // Don't highlight during typing - just let the user type
      // The mention will be highlighted when they select a provider
      // This prevents issues with contentEditable="false" interfering with typing
    },

    escapeHtml(text) {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },

    placeCaretAfterMention() {
      const editor = this.$refs.editorRef;
      const sel = window.getSelection();
      const range = document.createRange();

      // Find the mention highlight span
      const spans = editor.querySelectorAll('.mention-highlight');
      if (spans.length > 0) {
        const span = spans[spans.length - 1];
        range.setStartAfter(span);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    },

    getCaretPosition(element) {
      const sel = window.getSelection();
      if (!sel.rangeCount) return 0;

      const range = sel.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(element);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      return preCaretRange.toString().length;
    },

    calculateDropdownPosition() {
      const sel = window.getSelection();
      if (!sel.rangeCount) return;

      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      // Get viewport dimensions for edge detection
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Dropdown dimensions
      const dropdownWidth = 300;
      const dropdownHeight = 250; // max-height

      // Calculate initial position (5px below cursor)
      let top = rect.bottom + 5;
      let left = rect.left;

      // Edge Case 1: Dropdown would go below viewport
      // Position above cursor instead
      if (top + dropdownHeight > viewportHeight - 20) {
        top = rect.top - dropdownHeight - 5;

        // Safety check: If still off-screen top, clamp to top
        if (top < 10) {
          top = 10;
        }
      }

      // Edge Case 2: Dropdown would go beyond right edge
      // Align to right edge
      if (left + dropdownWidth > viewportWidth - 20) {
        left = viewportWidth - dropdownWidth - 20;
      }

      // Edge Case 3: Dropdown would go beyond left edge
      if (left < 10) {
        left = 10;
      }

      this.dropdownPosition = {
        top,
        left
      };
    },

    selectMention(customer) {
      const editor = this.$refs.editorRef;
      const sel = window.getSelection();

      if (!sel.rangeCount) return;

      // Find the text node and offsets where @ symbol is
      const mentionInfo = this.findMentionTextNode();
      if (!mentionInfo) {
        // Fallback to old behavior if findMentionTextNode fails
        const text = editor.innerText || '';
        const beforeMention = text.substring(0, this.mentionStartOffset);
        const afterMention = text.substring(this.mentionStartOffset + 1 + this.searchTerm.length);
        const newText = beforeMention + '@' + customer.name + ' ' + afterMention;
        const newHtml = this.escapeHtml(beforeMention) +
          '<span class="mention-highlight" contenteditable="false">@' + this.escapeHtml(customer.name) + '</span> ' +
          this.escapeHtml(afterMention);

        editor.innerHTML = newHtml;
        this.editorContent = newText;
        this.$emit('update:modelValue', newText);
        this.mentions.push({
          customer_id: customer.customer_id,
          name: customer.name,
          clinic_id: this.userInfo?.clinic_id || ''
        });
        this.showDropdown = false;
        this.$nextTick(() => {
          this.placeCaretAtEnd();
        });
        return;
      }

      const { textNode, startOffset, endOffset } = mentionInfo;

      // Create the mention span element
      const mentionSpan = document.createElement('span');
      mentionSpan.className = 'mention-highlight';
      mentionSpan.textContent = '@' + customer.name;
      mentionSpan.dataset.customerId = customer.customer_id;
      mentionSpan.dataset.clinicId = this.userInfo?.clinic_id || '';
      // IMPORTANT: Make the mention span non-editable so it behaves as a single unit
      mentionSpan.contentEditable = "false";

      // Create a text node for the space after mention
      const spaceNode = document.createTextNode(' ');

      // Create a range to select the text to replace (@ + search term)
      const replaceRange = document.createRange();
      replaceRange.setStart(textNode, startOffset);
      replaceRange.setEnd(textNode, endOffset);

      // Delete the @ + search term
      replaceRange.deleteContents();

      // Insert the mention and space
      replaceRange.insertNode(mentionSpan);
      replaceRange.setStartAfter(mentionSpan);
      replaceRange.insertNode(spaceNode);

      // Move cursor after the space
      const newRange = document.createRange();
      newRange.setStartAfter(spaceNode);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);

      // Build backend format from the entire DOM state
      // This preserves text before and after the mention
      const backendContent = this.buildBackendFormat();
      this.editorContent = backendContent;

      // Emit to update v-model with backend format
      this.$emit('update:modelValue', backendContent);

      // Track this mention for later reference
      this.mentions.push({
        customer_id: customer.customer_id,
        name: customer.name,
        clinic_id: this.userInfo?.clinic_id || ''
      });

      // Hide dropdown
      this.showDropdown = false;

      // Focus editor
      editor.focus();
    },

    placeCaretAtPosition(pos) {
      const editor = this.$refs.editorRef;
      const sel = window.getSelection();
      const range = document.createRange();

      let charCount = 0;
      let found = false;

      function traverseNodes(node) {
        if (found) return;

        if (node.nodeType === Node.TEXT_NODE) {
          const nextCount = charCount + node.length;
          if (pos >= charCount && pos <= nextCount) {
            range.setStart(node, pos - charCount);
            range.collapse(true);
            found = true;
          }
          charCount = nextCount;
        } else {
          for (let child of node.childNodes) {
            traverseNodes(child);
            if (found) break;
          }
        }
      }

      traverseNodes(editor);

      if (found) {
        sel.removeAllRanges();
        sel.addRange(range);
        editor.focus();
      }
    },

    findMentionTextNode() {
      const editor = this.$refs.editorRef;
      if (!editor) return null;

      const mentionStartIndex = this.mentionStartOffset;
      const mentionEndIndex = mentionStartIndex + 1 + this.searchTerm.length;

      let targetNode = null;
      let startOffset = -1;
      let endOffset = -1;
      let charCount = 0;

      function traverse(node) {
        if (targetNode) return;

        if (node.nodeType === Node.TEXT_NODE) {
          const nodeLength = node.length;

          // Check if @ symbol is within this node
          if (charCount <= mentionStartIndex && charCount + nodeLength >= mentionStartIndex) {
            targetNode = node;
            startOffset = mentionStartIndex - charCount;
            endOffset = mentionEndIndex - charCount;
            return;
          }

          charCount += nodeLength;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Skip mention highlight spans (already inserted mentions)
          if (node.classList && node.classList.contains('mention-highlight')) {
            charCount += node.textContent.length;
            return;
          }

          for (let child of node.childNodes) {
            traverse(child);
            if (targetNode) return;
          }
        }
      }

      traverse(editor);

      if (targetNode) {
        return { textNode: targetNode, startOffset, endOffset };
      }
      return null;
    },

    // Transform content for backend submission
    // Converts "@Vibrant IT3333" to "@doctor_999997_clinicId"
    transformContent(displayContent) {
      const editor = this.$refs.editorRef;
      if (!editor) return displayContent;

      // Find all mention spans in the editor
      const mentionSpans = editor.querySelectorAll('.mention-highlight');

      if (mentionSpans.length === 0) {
        return editor.innerText || displayContent;
      }

      // Get the current plain text content from editor
      const plainText = editor.innerText || '';

      // Build a list of mentions in order of appearance
      const foundMentions = Array.from(mentionSpans).map(span => {
        const mention = {
          customer_id: span.dataset.customerId,
          clinic_id: span.dataset.clinicId,
          name: span.textContent.substring(1), // Remove @ symbol
          displayText: span.textContent
        };
        return mention;
      });

      // Transform the content by replacing each mention with backend format
      let transformed = plainText;

      // Replace mentions one by one in reverse order to preserve indices
      foundMentions.reverse().forEach(mention => {
        const escapedName = this.escapeRegex(mention.name);
        const regex = new RegExp(`@${escapedName}`, 'g');
        const backendFormat = `@doctor_${mention.customer_id}_${mention.clinic_id}`;
        transformed = transformed.replace(regex, backendFormat);
      });

      return transformed;
    },

    // Helper to escape special regex characters
    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },

    // Get display text from current editor content
    getDisplayTextFromContent(hasMentionSpans) {
      const editor = this.$refs.editorRef;
      if (!editor) return '';

      const text = editor.innerText || '';

      if (!hasMentionSpans) {
        // No mentions, return plain text
        return text;
      }

      // Has mentions, convert backend format to display text
      const mentionRegex = /@doctor_(\w+)_\w+/g;
      return text.replace(mentionRegex, (match, customerId) => {
        const providerName = this.customerNameMap[customerId];
        return providerName ? `@${providerName}` : match;
      });
    },

    // ============ Image Upload Methods ============

    // Handle drag over events
    handleDragOver(event) {
      event.preventDefault();
    },

    // Handle drag leave events
    handleDragLeave(event) {
      event.preventDefault();
    },

    // Handle drop events for image files
    handleDrop(event) {
      const files = event.dataTransfer?.files;

      if (!files || files.length === 0) return;

      // Process each dropped file
      Array.from(files).forEach(file => {
        if (this.isValidImageFile(file)) {
          this.insertInlineImage(file);
        }
      });
    },

    // Handle paste events for inline images
    handlePaste(event) {
      const clipboardData = event.clipboardData;
      if (!clipboardData) return;

      const items = clipboardData.items;

      // Check for image in clipboard
      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.type.indexOf('image') !== -1) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file && this.isValidImageFile(file)) {
            this.insertInlineImage(file);
          }
          return;
        }
      }

      // If no image, let default paste behavior happen
      // We need to manually allow the paste since we used preventDefault above
      // But actually we only preventDefault if we found an image
      // So for text paste, we should NOT call preventDefault
      // Let me refactor this...

      // Actually, the issue is we have @paste.prevent="handlePaste" in template
      // So we need to manually insert text if no image
      this.handleTextPaste(event);
    },

    // Handle text paste (when no image)
    handleTextPaste(event) {
      const clipboardData = event.clipboardData;
      if (!clipboardData) return;

      const text = clipboardData.getData('text/plain');
      if (text) {
        event.preventDefault();
        document.execCommand('insertText', false, text);
      }
    },

    // Validate image file
    isValidImageFile(file) {
      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        notify({
          title: 'Invalid File Type',
          message: 'Only JPG, PNG, GIF, and WebP images are supported.',
          type: 'error',
          duration: 3000
        });
        return false;
      }

      // Check file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        notify({
          title: 'File Too Large',
          message: 'Image size must be less than 10MB.',
          type: 'error',
          duration: 3000
        });
        return false;
      }

      return true;
    },

    // Insert image inline as base64
    insertInlineImage(file) {
      const editor = this.$refs.editorRef;

      // Count existing images in editor
      const existingImages = editor.querySelectorAll('img').length;
      const maxImages = 5;

      if (existingImages >= maxImages) {
        notify({
          title: 'Maximum Images Reached',
          message: `You can only upload up to ${maxImages} images per note.`,
          type: 'error',
          duration: 1500
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        const img = document.createElement('img');
        img.src = base64;
        img.style.maxWidth = '200px';
        img.style.maxHeight = '200px';
        img.style.display = 'inline';
        img.style.verticalAlign = 'middle';
        img.style.margin = '0 4px';

        // Focus editor first
        editor.focus();

        // Insert at current cursor position using Selection API
        const sel = window.getSelection();

        if (sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          range.deleteContents();
          range.insertNode(img);

          // Move cursor after the image
          range.setStartAfter(img);
          range.setEndAfter(img);
          sel.removeAllRanges();
          sel.addRange(range);
        } else {
          // No cursor position, append to end
          editor.appendChild(img);
        }

        // Ensure cursor is visible
        const imgRect = img.getBoundingClientRect();
        const editorRect = editor.getBoundingClientRect();

        // Scroll to cursor if needed
        if (imgRect.bottom > editorRect.bottom) {
          img.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }

        // Emit the HTML content
        this.$emit('update:modelValue', editor.innerHTML);
      };
      reader.readAsDataURL(file);
    },
  },
};
</script>

<style scoped>
.mention-textarea-wrapper {
  position: relative;
  z-index: 1;
  min-height: 60px;
}

.mention-editor {
  padding: 0;
  color: var(--TrueBlack, #333333);
  font-family: "IBM Plex Sans Hebrew", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.15px;
  border: none;
  background: transparent;
  overflow-y: auto;
  overflow-x: hidden;
  white-space: pre-wrap;
  word-wrap: break-word;
  outline: none;
  min-height: 60px;
  max-height: 120px;
  direction: ltr;
  text-align: left;
}

/* Scrollbar for editor */
.mention-editor::-webkit-scrollbar {
  width: 8px;
}

.mention-editor::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.mention-editor::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.mention-editor:empty:before {
  content: attr(data-placeholder);
  color: var(--ContentMediumGrey, #999999);
  pointer-events: none;
}

.mention-editor:focus {
  outline: none;
}

:deep(.mention-highlight) {
  color: var(--OldVibrantGreen, #25B8C2);
  font-family: "IBM Plex Sans Hebrew", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  white-space: nowrap;
  user-select: none;
  border-radius: 3px;
  padding: 0 2px;
  margin: 0 1px;
}

.mention-dropdown {
  position: fixed;
  z-index: 100000;
  background: white;
  border: 1px solid #D7E3EC;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 250px;
  overflow-y: auto;
  width: 300px;
  padding: 4px 0;
}

.mention-no-results {
  padding: 12px 16px;
  color: var(--ContentMediumGrey, #999999);
  font-size: 14px;
  text-align: center;
}

.mention-item {
  padding: 10px 16px;
  cursor: pointer;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: var(--OldVibrantGreen, #25B8C2);
  transition: background-color 0.15s ease;
}

.mention-item:hover,
.mention-item-selected {
  background-color: #F0F7FF;
}

.mention-dropdown::-webkit-scrollbar {
  width: 6px;
}

.mention-dropdown::-webkit-scrollbar-thumb {
  background-color: #C4C4C4;
  border-radius: 3px;
}

.mention-dropdown::-webkit-scrollbar-thumb:hover {
  background-color: #999999;
}
</style>
